import { Controller } from "@hotwired/stimulus"

const CYCLE_KEY = "floosyo:guide-cycle"

// One pick per page view, shared by every panel on it. The carousel renders a
// guide strip per empty budget, so without this the cycle would step once per
// panel and skip characters (1, 3, 5, 2...), and two panels would disagree
// about who is speaking. Cleared when a visit starts, so the next screen draws
// the next character.
let pickThisVisit = null
const clearPick = () => { pickThisVisit = null }
document.addEventListener("turbo:visit", clearPick)
document.addEventListener("turbo:before-render", clearPick)

// The mascot's opening line: a different character every time the screen opens,
// and the line typed out word by word as you arrive.
//
// Why the cycle runs here and not on the server: the native shell restores this
// screen from Turbo's snapshot cache without asking Rails, so a server-side
// counter hands back the same character on every cached visit. The server picks
// the first paint (and is the whole story without JS); this steps the cycle
// forward on every connect, cached or not. 1, 2, 3, 4, 5, round again.
//
// Nothing is hidden while it waits. The strip and the mascot are on screen from
// the first paint — an empty box would be worse than no animation — and only
// the words arrive one at a time. They are split into spans up front and
// revealed with opacity alone, so the strip is its final size from the first
// frame and the locked sections below it never move.
//
// Two gates before the words start: window load, so a cold launch does not
// spend the animation behind the native loading overlay, and intersection, so a
// budget panel the carousel is holding off-screen types when it is swiped to.
export default class extends Controller {
  static targets = ["mascot", "line"]
  static values = {
    variants: { type: Array, default: [] },
    settle: { type: Number, default: 260 },
    speed: { type: Number, default: 58 }
  }

  connect() {
    this.timers = []
    this.typed = false

    this.#applyVariant()
    this.#splitIntoWords()

    if (this.#prefersReducedMotion()) {
      this.words.forEach((word) => word.classList.add("is-in"))
      return
    }

    this.#whenLoaded(() => this.#observe())
  }

  disconnect() {
    this.timers.forEach((id) => clearTimeout(id))
    this.timers = []
    this.observer?.disconnect()
  }

  // -- who is speaking --

  #applyVariant() {
    const variants = this.variantsValue
    if (variants.length === 0) return

    const variant = variants[this.#nextIndex(variants.length)]
    if (!variant) return

    if (this.hasLineTarget) this.lineTarget.textContent = variant.line
    if (this.hasMascotTarget && variant.mascot) this.mascotTarget.src = variant.mascot
  }

  #nextIndex(size) {
    if (pickThisVisit !== null) return pickThisVisit % size

    try {
      const index = (parseInt(localStorage.getItem(CYCLE_KEY), 10) || 0) % size
      localStorage.setItem(CYCLE_KEY, String((index + 1) % size))
      pickThisVisit = index
      return index
    } catch (_) {
      // Private mode or storage disabled: the server's pick stands.
      pickThisVisit = 0
      return 0
    }
  }

  // -- gates --

  #whenLoaded(callback) {
    if (document.readyState === "complete") return callback()

    window.addEventListener("load", () => callback(), { once: true })
  }

  #observe() {
    if (typeof IntersectionObserver === "undefined") return this.#type()

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || this.typed) return
        this.observer.disconnect()
        this.#type()
      })
    }, { threshold: 0.35 })

    this.observer.observe(this.element)
  }

  // -- the line --

  #type() {
    if (this.typed) return
    this.typed = true

    this.words.forEach((word, index) => {
      this.timers.push(
        setTimeout(() => word.classList.add("is-in"), this.settleValue + index * this.speedValue)
      )
    })
  }

  // Real text nodes between the spans, so the line wraps exactly as it would
  // have unsplit.
  #splitIntoWords() {
    this.words = []
    if (!this.hasLineTarget) return

    const line = this.lineTarget.textContent.trim()
    if (!line) return

    const parts = line.split(/\s+/)
    const fragment = document.createDocumentFragment()

    this.words = parts.map((part, index) => {
      const span = document.createElement("span")
      span.className = "guide-word"
      span.textContent = part
      fragment.appendChild(span)
      if (index < parts.length - 1) fragment.appendChild(document.createTextNode(" "))
      return span
    })

    this.lineTarget.textContent = ""
    this.lineTarget.appendChild(fragment)
  }

  #prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }
}
